// ============================================
// ENUMS
// ============================================

export enum Role {
  PRIMARY = 'PRIMARY',
  MEMBER = 'MEMBER',
}

export enum EventType {
  FEED = 'FEED',
  NAPPY = 'NAPPY',
  SPIT_UP = 'SPIT_UP',
  MEDICATION = 'MEDICATION',
  SLEEP = 'SLEEP',
  WEIGHT = 'WEIGHT',
  TEMPERATURE = 'TEMPERATURE',
  TUMMY_TIME = 'TUMMY_TIME',
  PUMP = 'PUMP',
  BOTTLE = 'BOTTLE',
  JOURNAL = 'JOURNAL',
  EC = 'EC',
  // Pregnancy
  SYMPTOM = 'SYMPTOM',
  APPOINTMENT = 'APPOINTMENT',
  BLOOD_PRESSURE = 'BLOOD_PRESSURE',
  ULTRASOUND = 'ULTRASOUND',
  KICK_COUNT = 'KICK_COUNT',
  CONTRACTION = 'CONTRACTION',
}

export enum ProfileType {
  BABY = 'BABY',
  PREGNANCY = 'PREGNANCY',
}

export enum TicketStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  AWAITING_RESPONSE = 'AWAITING_RESPONSE',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

export enum Priority {
  LOW = 'LOW',
  NORMAL = 'NORMAL',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

export enum TicketType {
  PROBLEM = 'PROBLEM',
  FEATURE_REQUEST = 'FEATURE_REQUEST',
}

export enum EmergencyStatus {
  PENDING = 'PENDING',
  GRANTED = 'GRANTED',
  DENIED = 'DENIED',
  EXPIRED = 'EXPIRED',
  REVOKED = 'REVOKED',
}

// ============================================
// API RESPONSE TYPES
// ============================================

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  pagination?: PaginationMeta;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ============================================
// USER TYPES
// ============================================

export interface User {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  publicKey: string;
  encryptedPrivateKey: string;
  createdAt: string;
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

export interface LoginResponse {
  user: User;
  tokens: TokenPair;
}

// ============================================
// BABY TYPES
// ============================================

export interface Baby {
  id: string;
  encryptedData: string;
  profileType: ProfileType;
  role: Role;
  encryptedBabyKey: string;
  profilePhotoId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DecryptedBaby {
  id: string;
  profileType: ProfileType;
  name: string;
  // Baby-specific
  dateOfBirth?: Date;
  gender?: 'male' | 'female' | 'other';
  pediatrician?: {
    name: string;
    phone?: string;
    address?: string;
  };
  dailyFeedTargetMl?: number;
  dailyFeedTargetMinMl?: number;
  dailyFeedTargetMaxMl?: number;
  postFeedUprightMinutes?: number;
  // Pregnancy-specific
  dueDate?: string;
  prePregnancyWeight?: number;
  prePregnancyWeightUnit?: 'kg' | 'lbs';
  obGyn?: {
    name: string;
    phone?: string;
    address?: string;
  };
  // Shared
  photoUrl?: string;
  allergies?: string[];
  medications?: string[];
  bloodType?: string;
  notes?: string;
  role: Role;
  createdAt: Date;
  updatedAt: Date;
}

export interface BabyMember {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  role: Role;
  createdAt: string;
}

// ============================================
// EVENT TYPES
// ============================================

export interface Event {
  id: string;
  type: EventType;
  timestamp: string;
  encryptedData: string;
  encryptedNotes: string | null;
  photos: Photo[];
  createdById: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface DecryptedEvent {
  id: string;
  type: EventType;
  timestamp: Date;
  data: EventData;
  notes: string | null;
  photos: Photo[];
  createdById: string;
  createdByName: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface EventData {
  // Feed
  feedType?: 'breast' | 'formula' | 'both' | 'bottle'; // bottle for backwards compatibility
  side?: 'left' | 'right' | 'both';
  duration?: number;
  amount?: number;
  startTime?: string;
  endTime?: string;

  // Nappy
  wet?: boolean;
  dirty?: boolean;
  rash?: boolean;
  rashSeverity?: number;
  blowout?: boolean;

  // Spit up
  severity?: 'little' | 'some' | 'lots';

  // Medication
  medicationName?: string;
  dose?: string;
  nextDue?: string;

  // Sleep
  sleepType?: 'night' | 'nap';
  location?: string;

  // Weight
  weight?: number;
  unit?: 'kg' | 'lbs';
  height?: number;
  heightUnit?: 'cm' | 'inches';
  headCircumference?: number;

  // Temperature
  temperature?: number;
  tempUnit?: 'C' | 'F';
  method?: 'oral' | 'rectal' | 'forehead' | 'armpit' | 'ear';

  // Tummy time
  mood?: 'happy' | 'neutral' | 'fussy';
  milestones?: string[];

  // Pump
  leftAmount?: number;
  rightAmount?: number;
  storageLocation?: string;

  // Bottle
  bottleType?: 'formula' | 'expressed';
  amountOffered?: number;
  amountConsumed?: number;
  formulaBrand?: string;

  // Journal
  title?: string;
  tags?: string[];
  isMilestone?: boolean;

  // EC (Elimination Communication)
  ecPee?: 'none' | 'little' | 'loads';
  ecPoop?: 'none' | 'little' | 'loads';

  // Tummy time (head-up duration in seconds, in addition to total duration in seconds)
  headUpDuration?: number;

  // Symptom (Pregnancy)
  symptomType?: string; // see AddEvent.tsx for full symptom list
  symptomSeverity?: 'mild' | 'moderate' | 'severe';

  // Appointment (Pregnancy)
  appointmentType?: string; // checkup, ultrasound, blood_test, other
  providerName?: string;
  appointmentLocation?: string;
  nextAppointmentDate?: string;

  // Blood Pressure (Pregnancy)
  systolic?: number;
  diastolic?: number;
  heartRate?: number;

  // Ultrasound (Pregnancy)
  ultrasoundType?: string; // dating, anatomy, growth, other
  gestationalAgeWeeks?: number;
  fetalHeartRate?: number;
  ultrasoundFindings?: string;

  // Kick Count (Pregnancy)
  kickCount?: number;

  // Contraction (Pregnancy)
  contractionDuration?: number; // seconds
  contractionIntervalMinutes?: number;
  contractionIntensity?: 'mild' | 'moderate' | 'strong';

  // Common
  notes?: string;
}

// ============================================
// PHOTO TYPES
// ============================================

export interface Photo {
  id: string;
  r2Key: string;
  encryptedKey: string;
  mimeType: string;
  size: number;
  createdAt: string;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  photoId: string;
  r2Key: string;
}

export interface DownloadUrlResponse {
  downloadUrl: string;
  expiresAt: string;
}

// ============================================
// INVITE TYPES
// ============================================

export interface Invite {
  id: string;
  token: string;
  email: string | null;
  role: Role;
  expiresAt: string;
  used: boolean;
  createdAt: string;
  createdByName: string;
  inviteUrl: string;
}

export interface InviteInfo {
  id: string;
  email: string | null;
  role: Role;
  expiresAt: string;
  createdByName: string;
  babyId: string;
}

// ============================================
// SHARE TYPES
// ============================================

export interface Share {
  id: string;
  token: string;
  eventTypes: EventType[] | null;
  dateFrom: string | null;
  dateTo: string | null;
  expiresAt: string;
  accessCount: number;
  maxAccessCount: number | null;
  revoked: boolean;
  recipientEmail: string | null;
  recipientName: string | null;
  createdAt: string;
  shareUrl: string;
}

export interface SharedData {
  encryptedKey: string;
  babyEncryptedData: string;
  sharedByName: string;
  eventTypes: EventType[] | null;
  dateFrom: string | null;
  dateTo: string | null;
  expiresAt: string;
  events: Event[];
}

// ============================================
// SUPPORT TYPES
// ============================================

export interface SupportTicket {
  id: string;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: Priority;
  ticketType: TicketType;
  hasActiveAccess: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  messages?: SupportMessage[];
}

export interface SupportMessage {
  id: string;
  message: string;
  isAdmin: boolean;
  authorName: string;
  createdAt: string;
  attachments?: SupportAttachment[];
}

export interface SupportAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  keyPacket: string | null;
  createdAt: string;
}

export interface AccessCodeResponse {
  accessCode: string;
  expiresAt: string;
}

// ============================================
// EMERGENCY TYPES
// ============================================

export interface EmergencyContact {
  id: string;
  email: string;
  name: string;
  relationship: string;
  isActive: boolean;
  createdAt: string;
}

export interface EmergencyRequest {
  id: string;
  status: EmergencyStatus;
  reason: string | null;
  expiresAt: string;
  grantedAt?: string;
  createdAt: string;
  contactName?: string;
}

export interface EmergencyPacket {
  babyName: string;
  dateOfBirth: string;
  bloodType?: string;
  allergies: string[];
  medications: string[];
  pediatrician?: {
    name: string;
    phone?: string;
    address?: string;
  };
  emergencyNotes?: string;
}

// ============================================
// STATS TYPES
// ============================================

export interface BabyStats {
  totalEvents: number;
  eventsByType: Partial<Record<EventType, number>>;
  eventsToday: number;
  lastEventByType: Partial<Record<EventType, string | null>>;
}
